/**
 * API Client - Lightweight fetch wrapper for Backend Custom API
 *
 * Features:
 * - Automatic JWT token handling from Supabase Auth
 * - Request timeout with AbortController
 * - Consistent error handling
 * - RESTful methods: GET, POST, PUT, DELETE, PATCH
 *
 * @module utils/apiClient
 *
 * @example
 * import { apiClient } from '@/utils/apiClient';
 *
 * // GET request
 * const { data, error } = await apiClient.get('/api/orders');
 *
 * // POST request
 * const { data, error } = await apiClient.post('/api/orders', {
 *   cart_items: [...],
 *   shipping_address: {...}
 * });
 */

import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

/**
 * API Client class for Backend Custom API requests
 */
class ApiClient {
  /**
   * @param {string} base_url - Base URL for API requests
   */
  constructor(base_url) {
    this.base_url = base_url;
    this.default_timeout = 10000; // 10 seconds
  }

  /**
   * Get authentication token from Supabase session
   * @private
   * @returns {Promise<string|null>} JWT access token or null
   */
  async getAuthToken() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        logger.warn('ApiClient', 'Failed to get auth session', { error: error.message });
        return null;
      }

      return session?.access_token || null;
    } catch (error) {
      logger.warn('ApiClient', 'Unexpected error getting auth token', { error: error.message });
      return null;
    }
  }

  /**
   * Execute HTTP request with timeout and auth
   * @private
   * @param {string} endpoint - API endpoint (e.g., '/api/orders')
   * @param {Object} options - Fetch options
   * @param {number} [options.timeout] - Request timeout in ms (default: 10000)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async request(endpoint, options = {}) {
    const url = `${this.base_url}${endpoint}`;
    const method = options.method || 'GET';
    const start_time = performance.now();

    // Log request start
    logger.debug('ApiClient', `Request: ${method} ${endpoint}`, {
      has_body: !!options.body,
    });

    try {
      // Get auth token automatically
      const token = await this.getAuthToken();

      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      };

      // Setup timeout with AbortController
      const controller = new AbortController();
      const timeout = setTimeout(
        () => controller.abort(),
        options.timeout || this.default_timeout
      );

      // Execute fetch request
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      // Parse response
      const response_data = await response.json();

      const duration_ms = Math.round(performance.now() - start_time);

      // Handle HTTP errors
      if (!response.ok) {
        const error_message = response_data.error || `HTTP ${response.status}: ${response.statusText}`;
        logger.error('ApiClient', `Failed: ${method} ${endpoint}`, {
          status: response.status,
          error: error_message,
          duration_ms,
        });
        return {
          success: false,
          error: error_message,
          data: null,
        };
      }

      // Success response
      logger.info('ApiClient', `Response: ${method} ${endpoint}`, {
        status: response.status,
        duration_ms,
      });
      return {
        success: true,
        data: response_data.data || response_data,
        error: null,
      };

    } catch (error) {
      const duration_ms = Math.round(performance.now() - start_time);

      // Handle timeout
      if (error.name === 'AbortError') {
        logger.error('ApiClient', `Timeout: ${method} ${endpoint}`, {
          timeout_ms: options.timeout || this.default_timeout,
          duration_ms,
        });
        return {
          success: false,
          error: 'Request timeout - please try again',
          data: null,
        };
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        logger.error('ApiClient', `Network error: ${method} ${endpoint}`, {
          error: error.message,
          duration_ms,
        });
        return {
          success: false,
          error: 'Network error - check your connection',
          data: null,
        };
      }

      // Handle unexpected errors
      logger.error('ApiClient', `Unexpected error: ${method} ${endpoint}`, {
        error: error.message,
        duration_ms,
      });
      return {
        success: false,
        error: error.message || 'Unexpected error occurred',
        data: null,
      };
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} [options] - Additional fetch options
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   *
   * @example
   * const { success, data, error } = await apiClient.get('/api/orders/123');
   * if (success) {
   *   console.log('Order:', data);
   * } else {
   *   console.error('Error:', error);
   * }
   */
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} [options] - Additional fetch options
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   *
   * @example
   * const { success, data, error } = await apiClient.post('/api/orders', {
   *   cart_items: [{ artwork_id: '123', quantity: 1 }],
   *   shipping_address: { street: 'Av. Libertador', city_id: 'santiago' }
   * });
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} [options] - Additional fetch options
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   *
   * @example
   * const { success, data, error } = await apiClient.put('/api/orders/123', {
   *   status: 'shipped'
   * });
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} [options] - Additional fetch options
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   *
   * @example
   * const { success, data, error } = await apiClient.patch('/api/orders/123', {
   *   tracking_number: 'ABC123'
   * });
   */
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} [options] - Additional fetch options
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   *
   * @example
   * const { success, data, error } = await apiClient.delete('/api/cart/items/123');
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(
  import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000'
);

// Export class for testing
export { ApiClient };
